import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, AlertTriangle, Clock } from "lucide-react";
import { api } from "../lib/api";
import { CATEGORY_BY_KEY, CATEGORIES } from "../lib/categories";

function fmtDate(iso) {
  if (!iso) return "-";
  try {
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
  } catch { return iso; }
}

export default function ExpiringList() {
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeKind = searchParams.get("kind") || "all";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/dashboard/home");
        if (alive) setAllItems(r.data?.upcoming_30d || []);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const items = useMemo(() => {
    if (activeKind === "all") return allItems;
    return allItems.filter((i) => i.kind === activeKind);
  }, [allItems, activeKind]);

  const setKindFilter = (k) => {
    if (k === "all") searchParams.delete("kind");
    else searchParams.set("kind", k);
    setSearchParams(searchParams, { replace: true });
  };

  const expired = items.filter((i) => i.status === "scaduto");
  const expiring = items.filter((i) => i.status !== "scaduto");

  const Row = ({ it }) => {
    const cat = CATEGORY_BY_KEY[it.kind] || {};
    const detailPath = cat.detailPath ? cat.detailPath(it.id) : "#";
    const badgeColor = it.status === "scaduto" ? "bg-[#FF3B30] text-white" : "bg-[#FFCC00] text-black";
    return (
      <Link
        to={detailPath}
        className="flex items-center justify-between gap-3 py-3 px-4 hover:bg-[#fff7f0] border-b border-neutral-200"
        data-testid={`exp-row-${it.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`shrink-0 px-2 py-0.5 rounded-full border-[1.5px] border-black text-[10px] font-bold ${badgeColor}`}>
            {it.status === "scaduto" ? "SCADUTO" : `${it.days_until}g`}
          </div>
          <div className="min-w-0">
            <div className="font-semibold truncate">{it.label || it.code}</div>
            <div className="text-xs text-neutral-500 truncate">
              {cat.label || it.kind} · {it.client_name || "—"}
              {it.category ? ` · ${it.category}` : ""}
            </div>
          </div>
        </div>
        <div className="text-right text-sm font-semibold tabular-nums">{fmtDate(it.expiration_date)}</div>
      </Link>
    );
  };

  return (
    <main className="min-h-screen max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10" data-testid="expiring-page">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 mb-4" data-testid="back-home">
        <ArrowLeft className="w-4 h-4" /> Torna alla Home
      </Link>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Scadenze imminenti</h1>

      {/* Category chips filter */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="exp-filter-chips">
        {[{ key: "all", label: `Tutte (${allItems.length})` }].concat(
          CATEGORIES.map((c) => ({
            key: c.kind,
            label: `${c.plural} (${allItems.filter((i) => i.kind === c.kind).length})`,
          }))
        ).map((chip) => (
          <button
            key={chip.key}
            type="button"
            onClick={() => setKindFilter(chip.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeKind === chip.key
                ? "bg-neutral-900 text-white"
                : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
            }`}
            data-testid={`exp-chip-${chip.key}`}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-neutral-500">Caricamento…</div>
      ) : items.length === 0 ? (
        <div className="bru-card p-8 text-center text-neutral-500">
          Nessuna scadenza nei prossimi 30 giorni.
        </div>
      ) : (
        <div className="space-y-6">
          {expired.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-base font-bold mb-2">
                <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                Già scaduti ({expired.length})
              </h2>
              <div className="bru-card overflow-hidden">
                {expired.map((it) => <Row key={`${it.kind}-${it.id}`} it={it} />)}
              </div>
            </section>
          )}
          {expiring.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-base font-bold mb-2">
                <Clock className="w-5 h-5 text-[#FFA000]" />
                Prossimi 30 giorni ({expiring.length})
              </h2>
              <div className="bru-card overflow-hidden">
                {expiring.map((it) => <Row key={`${it.kind}-${it.id}`} it={it} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </main>
  );
}
